// Import Dependencies
import { Fragment, useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";

// Local Imports
import { Button, Input, Textarea, Select } from "components/ui";
import { upsertMasterField } from "services/formBuilderService";
import {
  FiType,
  FiMail,
  FiPhone,
  FiHash,
  FiCalendar,
  FiChevronDown,
  FiFileText,
  FiCheckSquare,
  FiCircle,
  FiPaperclip,
  FiGlobe,
  FiClock,
  FiImage,
  FiStar,
  FiSliders,
  FiMapPin,
  FiDollarSign,
  FiPercent,
} from "react-icons/fi";

// Available field types with React Icons
const fieldTypeOptions = [
  { value: 1, label: "Text Input", htmlType: "text", icon: FiType, needsOptions: false, description: "Single line text input" },
  { value: 2, label: "Email", htmlType: "email", icon: FiMail, needsOptions: false, description: "Email address input with validation" },
  { value: 3, label: "Phone Number", htmlType: "tel", icon: FiPhone, needsOptions: false, description: "Phone number input" },
  { value: 4, label: "Number", htmlType: "number", icon: FiHash, needsOptions: false, description: "Numeric input only" },
  { value: 5, label: "Date", htmlType: "date", icon: FiCalendar, needsOptions: false, description: "Date picker input" },
  { value: 6, label: "Dropdown", htmlType: "select", icon: FiChevronDown, needsOptions: true, description: "Single select dropdown" },
  { value: 7, label: "Text Area", htmlType: "textarea", icon: FiFileText, needsOptions: false, description: "Multi-line text input" },
  { value: 8, label: "Checkbox Group", htmlType: "checkbox", icon: FiCheckSquare, needsOptions: true, description: "Multiple selection checkboxes" },
  { value: 9, label: "Radio Buttons", htmlType: "radio", icon: FiCircle, needsOptions: true, description: "Single selection radio buttons" },
  { value: 10, label: "File Upload", htmlType: "file", icon: FiPaperclip, needsOptions: false, description: "File upload input" },
  { value: 11, label: "Website URL", htmlType: "url", icon: FiGlobe, needsOptions: false, description: "URL input with validation" },
  { value: 12, label: "Time", htmlType: "time", icon: FiClock, needsOptions: false, description: "Time picker input" },
];

// ----------------------------------------------------------------------

export function CreateMasterFieldModal({
  isOpen,
  onClose,
  categoryId,
  fieldTypes = [],
  onSuccess,
  existingField = null
}) {
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("selection"); // "selection" or "configuration"
  const [selectedFieldType, setSelectedFieldType] = useState(null);
  const [formData, setFormData] = useState({
    fieldLabel: "",
    fieldTypeId: 1,
    fieldName: "",
    placeholder: "",
    isActive: true,
    defaultValue: "", // Keep for API compatibility but don't show in UI
    fieldOrder: 0,
    options: [],
    isCommon: true,
    categoryId: categoryId || "",
    isDeleted: false,
    id: null // Add id field for editing
  });
  const [optionInput, setOptionInput] = useState("");
  const isEditMode = !!existingField;

  // Update categoryId when prop changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, categoryId: categoryId || "" }));
  }, [categoryId]);

  // Pre-fill form data when editing existing field
  useEffect(() => {
    if (existingField && isOpen) {
      // Find the matching field type
      const matchingFieldType = availableFieldTypes.find(
        ft => ft.value === existingField.FieldTypeId
      );

      setFormData({
        id: existingField.Id,
        fieldLabel: existingField.FieldLabel || "",
        fieldTypeId: existingField.FieldTypeId || 1,
        fieldName: existingField.FieldName || "",
        placeholder: existingField.Placeholder || "",
        isActive: existingField.IsActive !== undefined ? existingField.IsActive : true,
        defaultValue: existingField.DefaultValue || "",
        fieldOrder: existingField.FieldOrder || 0,
        options: existingField.MasterFormFieldOptions?.map(opt => ({
          id: opt.Id || opt.id || crypto.randomUUID(),
          masterFormFieldId: opt.MasterFormFieldId || 0,
          optionLabel: opt.OptionLabel || opt.optionLabel || "",
          optionValue: opt.OptionValue || opt.optionValue || "",
          optionOrder: opt.OptionOrder || opt.optionOrder || 0
        })) || [],
        isCommon: existingField.IsCommon !== undefined ? existingField.IsCommon : true,
        categoryId: existingField.CategoryId || categoryId || "",
        isDeleted: false
      });

      if (matchingFieldType) {
        setSelectedFieldType(matchingFieldType);
        setView("configuration");
      }
    } else if (!isOpen) {
      // Reset when modal closes
      setView("selection");
      setSelectedFieldType(null);
    }
  }, [existingField, isOpen]);

  // Get available field types (use API field types if available, otherwise use defaults)
  const availableFieldTypes = fieldTypes.length > 0
    ? fieldTypes.map((ft, index) => ({
      value: ft.id || index + 1,
      label: ft.label || ft.typeName,
      htmlType: ft.value,
      icon: fieldTypeOptions.find(opt => opt.htmlType === ft.value)?.icon || FiType,
      needsOptions: ["select", "checkbox", "radio"].includes(ft.value),
      description: fieldTypeOptions.find(opt => opt.htmlType === ft.value)?.description || "Input field"
    }))
    : fieldTypeOptions;

  const needsOptions = selectedFieldType?.needsOptions || false;

  const handleFieldTypeSelect = (fieldType) => {
    setSelectedFieldType(fieldType);
    setFormData(prev => ({ ...prev, fieldTypeId: fieldType.value }));
    setView("configuration");
  };

  const handleBackToSelection = () => {
    setView("selection");
    setSelectedFieldType(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddOption = () => {
    if (!optionInput.trim()) {
      toast.error("Please enter an option label");
      return;
    }

    const newOption = {
      id: crypto.randomUUID ? crypto.randomUUID() : `option-${Date.now()}`,
      masterFormFieldId: 0,
      optionLabel: optionInput.trim(),
      optionValue: optionInput.trim(),
      optionOrder: formData.options.length + 1
    };

    setFormData(prev => ({
      ...prev,
      options: [...prev.options, newOption]
    }));
    setOptionInput("");
  };

  const handleRemoveOption = (optionId) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter(opt => opt.id !== optionId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.fieldLabel.trim()) {
      toast.error("Field label is required");
      return;
    }

    if (!formData.fieldName.trim()) {
      toast.error("Field name is required");
      return;
    }

    if (needsOptions && formData.options.length === 0) {
      toast.error("Please add at least one option for this field type");
      return;
    }

    setLoading(true);

    try {
      // Clean options: include id for the backend
      const cleanedOptions = formData.options.map(option => ({
        id: option.id,
        optionLabel: option.optionLabel,
        optionValue: option.optionValue,
        optionOrder: option.optionOrder
      }));

      const dataToSend = {
        ...formData,
        options: cleanedOptions
      };

      // Include Id in payload when editing
      if (isEditMode && formData.id) {
        dataToSend.id = formData.id;
      }

      const result = await upsertMasterField(dataToSend);

      if (result.success) {
        toast.success(result.message || (isEditMode ? "Master field updated successfully" : "Master field created successfully"));

        // Parse result.data if it's a string (SQL FOR JSON PATH returns string)
        let parsedData = result.data;
        if (typeof result.data === 'string') {
          try {
            parsedData = JSON.parse(result.data);
          } catch(e) {
            console.error("Failed to parse result.data", e);
          }
        }

        // Prepare the field data to pass back
        const createdFieldData = {
          Id: parsedData?.Id || parsedData?.id || formData.id,
          FieldLabel: formData.fieldLabel,
          FieldTypeId: formData.fieldTypeId,
          FieldName: formData.fieldName,
          Placeholder: formData.placeholder,
          DefaultValue: formData.defaultValue,
          HtmlInputType: selectedFieldType?.htmlType,
          MasterFormFieldOptions: cleanedOptions,
          IsCompanyMasterField: true // Assuming newly created fields are company master fields
        };

        // Call onSuccess with the created field data
        onSuccess && onSuccess(createdFieldData);

        // Only close modal if we're in edit mode or if we don't need to add immediately
        if (isEditMode) {
          handleClose();
        } else {
          // For new field creation, keep the modal open but reset for next creation
          // Or close it if that's your preference
          handleClose(); // You can remove this line if you want to keep modal open
        }
      } else {
        toast.error(result.message || (isEditMode ? "Failed to update master field" : "Failed to create master field"));
      }
    } catch (error) {
      console.error("Error creating master field:", error);
      toast.error("An error occurred while creating the field");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      fieldLabel: "",
      fieldTypeId: 1,
      fieldName: "",
      placeholder: "",
      isActive: true,
      defaultValue: "", // Keep for API compatibility
      fieldOrder: 0,
      options: [],
      isCommon: false,
      categoryId: categoryId || "",
      isDeleted: false,
      id: null
    });
    setOptionInput("");
    setView("selection");
    setSelectedFieldType(null);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100000] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-3xl origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 shadow-xl">
            {/* Header - Fixed */}
            <div className="border-b border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 px-6 py-4">
              <div className="flex items-center justify-between">
                {view === "selection" ? (
                  <>
                    <div>
                      <DialogTitle
                        as="h3"
                        className="text-lg font-semibold text-gray-900 dark:text-dark-100"
                      >
                        {isEditMode ? "Edit Master Field" : "Create Master Field"}
                      </DialogTitle>
                      <p className="text-sm text-gray-500 dark:text-dark-400 mt-0.5">
                        {isEditMode ? "Update the field properties" : "Select a field type to get started"}
                      </p>
                    </div>
                    <Button
                      onClick={handleClose}
                      variant="flat"
                      isIcon
                      className="size-8 rounded-md hover:bg-gray-100 dark:hover:bg-dark-600 cursor-pointer"
                    >
                      <XMarkIcon className="size-5 text-gray-500 dark:text-dark-400" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3 flex-1">

                      <div>
                        <DialogTitle
                          as="h3"
                          className="text-lg font-semibold text-gray-900 dark:text-dark-100"
                        >
                          {selectedFieldType?.label} Field
                        </DialogTitle>
                        <p className="text-sm text-gray-500 dark:text-dark-400 mt-0.5">
                          Configure the field properties
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleClose}
                      variant="flat"
                      isIcon
                      className="size-8 rounded-md hover:bg-gray-100 dark:hover:bg-dark-600 cursor-pointer"
                    >
                      <XMarkIcon className="size-5 text-gray-500 dark:text-dark-400" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Content - Scrollable */}
            {view === "selection" ? (
              /* Field Type Selection View */
              <>
                <div className="flex-1 overflow-y-auto beautiful-scrollbar p-6 min-h-[400px]">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {availableFieldTypes.map((fieldType) => {
                      const IconComponent = fieldType.icon;
                      return (
                        <button
                          key={fieldType.value}
                          onClick={() => handleFieldTypeSelect(fieldType)}
                          className="flex items-center gap-3 p-4 text-left bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-600 hover:shadow-sm transition-all group cursor-pointer"
                        >
                          <div className="flex-shrink-0 p-2.5 bg-primary-50 dark:bg-primary-900/30 rounded-lg group-hover:bg-primary-100 dark:group-hover:bg-primary-800/40 transition-colors">
                            <IconComponent className="size-5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm text-gray-900 dark:text-dark-100 mb-0.5">
                              {fieldType.label}
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-dark-400 line-clamp-1">
                              {fieldType.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Footer for Selection View */}
                <div className="border-t border-gray-200 dark:border-dark-600 px-6 py-4 bg-white dark:bg-dark-700">
                  <div className="flex justify-end">
                    <Button
                      onClick={handleClose}
                      variant="outlined"
                      className="min-w-[120px]"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* Field Configuration View */
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto beautiful-scrollbar p-6 min-h-[400px]">
                  <div className="space-y-5">
                    {/* Field Label */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1.5">
                        Field Label <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.fieldLabel}
                        onChange={(e) => handleInputChange("fieldLabel", e.target.value)}
                        placeholder="e.g., Full Name, Email Address"
                        required
                      />
                      <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                        Displayed to users in the form
                      </p>
                    </div>

                    {/* Field Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1.5">
                        Field Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.fieldName}
                        onChange={(e) => handleInputChange("fieldName", e.target.value)}
                        placeholder="e.g., full_name, email_address"
                        required
                        className="font-mono"
                      />
                      <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                        Internal identifier (lowercase with underscores)
                      </p>
                    </div>

                    {/* Placeholder */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1.5">
                        Placeholder <span className="text-xs text-gray-400">(Optional)</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.placeholder}
                        onChange={(e) => handleInputChange("placeholder", e.target.value)}
                        placeholder="e.g., Enter your full name"
                      />
                      <p className="text-xs text-gray-500 dark:text-dark-400 mt-1">
                        Hint text shown in empty fields
                      </p>
                    </div>

                    {/* Options - Only show for select, checkbox, radio */}
                    {needsOptions && (
                      <div className="pt-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1.5">
                          Options <span className="text-red-500">*</span>
                        </label>
                        <div className="space-y-3">
                          {/* Add Option Input */}
                          <div className="flex gap-2.5">
                            <Input
                              type="text"
                              value={optionInput}
                              onChange={(e) => setOptionInput(e.target.value)}
                              placeholder="Type option and press Enter"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddOption();
                                }
                              }}
                            />
                            <Button
                              type="button"
                              onClick={handleAddOption}
                              className="whitespace-nowrap"
                            >
                              Add
                            </Button>
                          </div>

                          {/* Options List */}
                          {formData.options.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500 dark:text-dark-400">
                                {formData.options.length} option{formData.options.length !== 1 ? 's' : ''}
                              </p>
                              <div className="space-y-2 max-h-56 overflow-y-auto custom-scrollbar">
                                {formData.options.map((option, index) => (
                                  <div
                                    key={option.id}
                                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-600 rounded-lg border border-gray-200 dark:border-dark-500 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
                                  >
                                    <div className="flex items-center gap-2.5 flex-1">
                                      <span className="flex items-center justify-center w-6 h-6 rounded bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 text-xs font-semibold">
                                        {index + 1}
                                      </span>
                                      <span className="text-sm font-medium text-gray-900 dark:text-dark-100">
                                        {option.optionLabel}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveOption(option.id)}
                                      className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 px-2.5 py-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-8 px-4 bg-gray-50 dark:bg-dark-600 rounded-lg border-2 border-dashed border-gray-300 dark:border-dark-500">
                              <p className="text-sm text-gray-500 dark:text-dark-400">
                                No options added. Add at least one to continue.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer - Fixed */}
                <div className="border-t border-gray-200 dark:border-dark-600 px-6 py-4 bg-white dark:bg-dark-700">
                  <div className="max-w-3xl mx-auto flex justify-end items-center gap-3">
                    <Button
                      type="button"
                      onClick={handleClose}
                      variant="outlined"
                      disabled={loading}
                      className="min-w-[120px]"
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={loading || (needsOptions && formData.options.length === 0)}
                      className="cursor-pointer min-w-[160px]"
                    >
                      {loading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Master Field" : "Create Master Field")}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

